import { saveOfferEntry } from "../../../offer-entry";
import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { apiRequest } from "../../../../../shared/api/client";
import { existingOfferReference, findDuplicateSupplierOffer, validateOfferEntry } from "../../../domain/offer-entry";
import type { Offer, RequestDetailData, RequestOfferEvaluation } from "../../../domain/request-detail-types";
import type { RequestDetailScope } from "./useRequestDetailScope";
import type { useRequestOfferForm } from "./useRequestOfferForm";

type Options = {detail: RequestDetailData | null; setDetail: Dispatch<SetStateAction<RequestDetailData | null>>; setEvaluation: Dispatch<SetStateAction<RequestOfferEvaluation | null>>};
export function useRequestOfferMutations(scope: RequestDetailScope, form: ReturnType<typeof useRequestOfferForm>, {detail, setDetail, setEvaluation}: Options) {
  const busy = useRef(new Set<RequestDetailScope>()), operation = useRef<object | null>(null), read = useRef(0), evaluationRead = useRef(0);
  const [status, setStatus] = useState<{scope: RequestDetailScope; saving: boolean; removing: string | null; pending: string | null}>({scope, saving: false, removing: null, pending: null});
  const visible = status.scope === scope ? status : {scope, saving: false, removing: null, pending: null};
  const setPendingOfferRemovalId: Dispatch<SetStateAction<string | null>> = value => {
    if (!scope.isCurrent()) return;
    setStatus(current => {const previous = current.scope === scope ? current : {scope, saving: false, removing: null, pending: null}; return {...previous, pending: typeof value === "function" ? value(previous.pending) : value};});
  };
  async function refresh(current: object, evaluation = true) {
    if (!scope.isCurrent() || operation.current !== current) return;
    const version = ++read.current;
    const evaluationVersion = evaluation ? ++evaluationRead.current : 0;
    const data = await apiRequest<RequestDetailData>(`/api/requests/${encodeURIComponent(scope.requestNo)}`);
    const isCurrent = () => scope.isCurrent() && operation.current === current && version === read.current;
    if (isCurrent()) setDetail(data);
    if (evaluation) {
      const result = await apiRequest<RequestOfferEvaluation>(`/api/requests/${encodeURIComponent(scope.requestNo)}/evaluations/latest`).catch(() => null);
      if (scope.isCurrent() && operation.current === current && evaluationVersion === evaluationRead.current) setEvaluation(result);
      return Boolean(result);
    }
    return false;
  }
  const saveDirectOffer = async () => {
    if (!scope.isCurrent() || busy.current.has(scope) || !form.isCurrentDraft() || detail?.id !== scope.requestNo) return;
    form.setOfferFormError("");
    const duplicate = findDuplicateSupplierOffer((detail.offers ?? []).map(offer => ({id: offer.id, requestNo: scope.requestNo, supplierId: offer.supplier_id, offerNo: offer.offer_no})), scope.requestNo, form.offerForm.supplierId, form.editingOfferInternalId);
    const error = duplicate ? `Bu tedarikçinin bu talep için zaten ${existingOfferReference(duplicate)} kaydı var.` : validateOfferEntry({requestNo: scope.requestNo, ...form.offerForm}) ?? (form.offerDocument && form.offerDocument.size > 20 * 1024 * 1024 ? "Teklif belgesi en fazla 20 MB olabilir." : null);
    if (error) {form.setOfferFormError(error); scope.notify(`Hata: ${error}`); return;}
    const current = {}; operation.current = current; busy.current.add(scope);
    setStatus(previous => ({...previous, scope, saving: true, removing: null}));
    let savedId: string | null = null;
    try {
      const {saved, documentUpload} = await saveOfferEntry<{id: string}>({input: {requestNo: scope.requestNo, ...form.offerForm}, editingOfferId: form.editingOfferId, document: form.offerDocument});
      savedId = saved.id;
      // Register rejection handling immediately, even if refreshing the saved row fails.
      if (documentUpload) void documentUpload.then(async () => {
        if (!scope.isCurrent()) return;
        try {await refresh(current, false); if (operation.current === current) scope.notify(`${saved.id} numaralı teklifin belgesi kullanıma hazır.`);}
        catch (error) {scope.notify(`${saved.id} numaralı teklifin belgesi yüklendi, ancak ekran yenilenemedi: ${error instanceof Error ? error.message : "Yenileme hatası."}`);}
      }, error => scope.notify(`${saved.id} numaralı teklif kaydedildi, ancak belge eklenemedi: ${error instanceof Error ? error.message : "Belge yükleme hatası."}`));
      if (!scope.isCurrent()) return;
      form.finishSaved();
      scope.notify(documentUpload ? `${form.editingOfferId ? "Teklif güncellendi" : `${saved.id} numaralı teklif kaydedildi`}; belge arka planda yükleniyor.` : form.editingOfferId ? "Teklif güncellendi." : `${saved.id} numaralı teklif kaydedildi.`);
      const evaluationRefreshed = await refresh(current);
      if (scope.isCurrent() && (!evaluationRefreshed || !documentUpload)) {
        scope.notify(evaluationRefreshed
          ? `${saved.id} numaralı teklif kaydedildi; ticari karşılaştırma yenilendi.`
          : `${saved.id} numaralı teklif kaydedildi; ticari karşılaştırma henüz yenilenmedi. Teklifleri kontrol edip değerlendirmeyi hazırlayın.`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Teklif kaydedilemedi.";
      if (savedId) scope.notify(`${savedId} numaralı teklif kaydedildi, ancak ekran yenilenemedi: ${message}`);
      else {form.setOfferFormError(message); scope.notify(`Hata: ${message}`);}
    } finally {
      busy.current.delete(scope);
      if (scope.isCurrent()) setStatus(current => ({...current, saving: false}));
    }
  };
  const removeOffer = async (offer: Offer) => {
    if (!scope.isCurrent() || busy.current.has(scope) || detail?.id !== scope.requestNo || !detail.offers.some(item => item.id === offer.id)) return;
    const current = {}; operation.current = current; busy.current.add(scope);
    setStatus(previous => ({...previous, scope, saving: false, removing: offer.id}));
    let deleted = false;
    try {
      await apiRequest(`/api/offers/${encodeURIComponent(offer.id)}`, {method: "DELETE"}); deleted = true;
      if (!scope.isCurrent()) return;
      setPendingOfferRemovalId(null);
      await refresh(current);
      if (!scope.isCurrent()) return;
      scope.notify(`${offer.offer_no} numaralı teklif kaldırıldı; denetim izi korundu.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Teklif kaldırılamadı.";
      scope.notify(deleted ? `${offer.offer_no} numaralı teklif kaldırıldı, ancak ekran yenilenemedi: ${message}` : message);
    } finally {
      busy.current.delete(scope);
      if (scope.isCurrent()) setStatus(current => ({...current, removing: null}));
    }
  };
  return {savingOffer: visible.saving, removingOfferId: visible.removing, pendingOfferRemovalId: visible.pending, setPendingOfferRemovalId, saveDirectOffer, removeOffer};
}
